import { captureException, captureReactException, ErrorBoundary } from '@sentry/react'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { Suspense, useCallback, useMemo } from 'react'
import { type ClientOnErrorFunction, createBrowserRouter, type createMemoryRouter, redirect, type RouteObject } from 'react-router'
import { RouterProvider } from 'react-router/dom'

import { SuspenseLayout } from './components/SuspenseLayout'
import { AppPage } from './pages/AppPage'
import { BillingPage, loader as billingPageLoader } from './pages/billing/BillingPage'
import { InvoiceDetailPage, loader as invoiceDetailPageLoader } from './pages/billing/InvoiceDetailPage'
import { loader as platformFeeDetailPageLoader, PlatformFeeDetailPage } from './pages/billing/PlatformFeeDetailPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { EmployeesPage } from './pages/EmployeesPage'
import { FallbackErrorPage } from './pages/FallbackErrorPage'
import { HomePage, loader as homePageLoader } from './pages/HomePage'
import { NotFoundPage } from './pages/NotFoundPage'
import { PageErrorPage } from './pages/PageErrorPage'
import { loader as payrollDeductionsPageLoader, PayrollDeductionsPage } from './pages/PayrollDeductionsPage'
import { ProfilePage } from './pages/ProfilePage'
import { loader as resourcesPageLoader, ResourcesPage } from './pages/ResourcesPage'
import { RootErrorPage } from './pages/RootErrorPage'
import { AllowanceSettingsPage } from './pages/settings/AllowanceSettingsPage'
import { BankingSettingsPage } from './pages/settings/BankingSettingsPage'
import { CompanySettingsPage } from './pages/settings/CompanySettingsPage'
import { PayrollSettingsPage } from './pages/settings/PayrollSettingsPage'
import { SettingsPage } from './pages/SettingsPage'
import { createDashboardContextMiddleware } from './router_contexts'

type CreateBrowserRouterType = typeof createBrowserRouter
type CreateMemoryRouterType = typeof createMemoryRouter

function createRouterWith (
  queryClient: QueryClient,
  createRouter: CreateBrowserRouterType | CreateMemoryRouterType = createBrowserRouter,
  routerOptions?: Parameters<CreateBrowserRouterType>[1] | Parameters<CreateMemoryRouterType>[1],
  testRoute?: RouteObject
) {
  const pages: RouteObject[] = [
    {
      index: true,
      loader: homePageLoader,
      Component: HomePage,
    },
    {
      path: 'employees',
      Component: EmployeesPage,
    },
    {
      path: 'deductions',
      Component: PayrollDeductionsPage,
      loader: payrollDeductionsPageLoader,
    },
    {
      path: 'documents',
      Component: DocumentsPage,
    },
    {
      path: 'resources',
      loader: resourcesPageLoader,
      Component: ResourcesPage,
    },
    {
      path: 'invoices',
      children: [
        {
          index: true,
          Component: BillingPage,
          loader: billingPageLoader,
        },
        {
          path: 'platform/:invoiceId',
          Component: PlatformFeeDetailPage,
          loader: platformFeeDetailPageLoader,
        },
        {
          path: 'employer/:invoiceId',
          Component: InvoiceDetailPage,
          loader: invoiceDetailPageLoader,
        },
      ]
    },
    {
      path: 'settings',
      Component: SettingsPage,
      children: [
        {
          path: '',
          loader: () => {
            throw redirect('company')
          }
        },
        {
          path: 'company',
          Component: CompanySettingsPage,
        },
        {
          path: 'allowances',
          Component: AllowanceSettingsPage,
        },
        {
          path: 'banking',
          Component: BankingSettingsPage,
        },
        {
          path: 'payroll',
          Component: PayrollSettingsPage,
        },
      ],
    },
    {
      path: 'profile',
      Component: ProfilePage,
    },
    {
      path: '*',
      Component: NotFoundPage,
    }
  ]

  if (import.meta.env.MODE === 'test' && testRoute) {
    pages.unshift(testRoute)
  }

  return createRouter([
    {
      path: 'employers/dashboard',
      Component: AppPage,
      // This error boundary will catch uncaught errors from this root route's
      // middleware, loader, hydrate fallback, and the `<AppPage />` component.
      // Errors in rendering the error boundary component will bubble up to the
      // "fallback" error boundary that lives outside the router.
      //
      // With errors caught at this level, we _do not_ have the app context we
      // need to render layout elements like the user's profile dropdown and the
      // navigation, so those elements are stubbed out in the UI.
      ErrorBoundary: RootErrorPage,
      HydrateFallback: SuspenseLayout,
      middleware: [createDashboardContextMiddleware(queryClient)],
      children: [
        {
          // This error boundary will catch uncaught errors from sub-routes'
          // loaders and components. At this level, we _do_ have the app context
          // we need to render layout elements like the user's profile dropdown
          // and navigation, so those elements are fully rendered in the UI.
          // It is only the `<main>` content that is replaced with the error UI.
          ErrorBoundary: PageErrorPage,
          children: pages,
        }
      ],
    },
  ], routerOptions)
}

interface EmployerDashboardAppProps {
  createRouter?: CreateBrowserRouterType | CreateMemoryRouterType;
  routerOptions?: Parameters<CreateBrowserRouterType>[1] | Parameters<CreateMemoryRouterType>[1];
  testRoute?: RouteObject;
}

export function EmployerDashboardApp (
  {
    createRouter: createRouterProp = createBrowserRouter,
    routerOptions,
    testRoute
  }: EmployerDashboardAppProps
) {
  const queryClient = useQueryClient()
  const router = useMemo(() => {
    return createRouterWith(queryClient, createRouterProp, routerOptions, testRoute)
  }, [])

  const handleError: ClientOnErrorFunction = useCallback((error, info) => {
    if (info.errorInfo) {
      captureReactException(error, info.errorInfo)
    } else {
      captureException(error)
    }
  }, [])

  return (
    <ErrorBoundary fallback={<FallbackErrorPage />}>
      <Suspense fallback={<SuspenseLayout />}>
        <RouterProvider onError={handleError} router={router} />
      </Suspense>
    </ErrorBoundary>
  )
}
